import { AgentAvatar, Banner, Button, Field, Select, Spinner } from "@salesko/design-system";
import type { AgentResearchMessagingChannel, Locale, SaleskoAgentSettings } from "@salesko/contracts";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Lock, Save, UserRound } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useI18n, type Dictionary } from "../../i18n";
import {
  exportTenantPrivacyZip,
  getAgentResearchEntitlement,
  getTenantAgentSettings,
  getTenantPolicy,
  updateTenantAgentSettings,
  updateTenantPolicy
} from "../../lib/api";
import type { StoredSaleskoSession } from "../../lib/api";
import { SettingsNote, SettingsSection } from "../settings/SettingsSection";
import { downloadBlobArtifact } from "../lib/download";

type TenantSettingsPanelProps = {
  enabled: boolean;
  session: StoredSaleskoSession | null;
};

type AgentSettingsDraft = {
  locale: Locale;
  messagingAddonEnabled: boolean;
  messagingChannels: AgentResearchMessagingChannel[];
  region: string;
  timezone: string;
};

const localeOptions: Locale[] = ["en", "zh-CN", "zh-TW"];
const regionOptionsByLocale: Record<Locale, string[]> = {
  en: ["US", "SG", "GB"],
  "zh-CN": ["CN"],
  "zh-TW": ["TW", "HK"]
};
const timezoneOptions = [
  "America/Los_Angeles",
  "Asia/Shanghai",
  "Asia/Taipei",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "Europe/London"
];
const messagingChannelOptions: AgentResearchMessagingChannel[] = ["telegram", "wechat", "line"];

const defaultAgentSettingsDraft: AgentSettingsDraft = {
  locale: "en",
  messagingAddonEnabled: false,
  messagingChannels: [],
  region: "US",
  timezone: "America/Los_Angeles"
};

/** Account-only settings page. No Agent or export query is mounted here. */
export function WorkspaceSettingsPanel({ enabled, session }: TenantSettingsPanelProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const tenantQueryScope = session?.tenantId ?? "anonymous";
  const tenantPolicyQueryKey = ["tenant-policy", tenantQueryScope] as const;
  const policyQuery = useQuery({
    queryKey: tenantPolicyQueryKey,
    queryFn: getTenantPolicy,
    enabled,
    retry: 1,
    staleTime: 30_000
  });
  const [nameDraft, setNameDraft] = useState("");
  const policyMutation = useMutation({
    mutationFn: (name: string) => updateTenantPolicy({ name }),
    onSuccess: (response) => {
      queryClient.setQueryData(tenantPolicyQueryKey, response);
    }
  });
  const policy = policyQuery.data?.policy ?? null;
  const nameChanged = Boolean(policy && nameDraft.trim() !== policy.name);
  const nameValid = nameDraft.trim().length > 0;

  useEffect(() => {
    const nextPolicy = policyQuery.data?.policy;
    if (nextPolicy) {
      setNameDraft(nextPolicy.name);
    }
  }, [policyQuery.data?.policy]);

  const handleNameSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!enabled || !nameChanged || !nameValid || policyMutation.isPending) {
      return;
    }
    policyMutation.mutate(nameDraft.trim());
  };

  return (
    <SettingsSection
      ariaLabel={t.tenantAdmin.workspaceAriaLabel}
      id="settings-account"
      title={t.tenantAdmin.workspaceTitle}
      trailing={policyQuery.isFetching ? <Spinner size={16} /> : <UserRound size={15} />}
    >
      {!enabled ? <SettingsNote>{t.tenantAdmin.signInWorkspace}</SettingsNote> : null}
      {policyQuery.isError ? <Banner status="error">{friendlyError(policyQuery.error, t)}</Banner> : null}
      {enabled && policyQuery.isLoading ? <Spinner label={t.tenantAdmin.loadingWorkspace} size={16} /> : null}
      {enabled && policy ? (
        <form onSubmit={handleNameSubmit} style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field
            label={t.tenantAdmin.workspaceName}
            maxLength={120}
            onChange={(value) => setNameDraft(value)}
            value={nameDraft}
          />
          <div>
            <Button
              disabled={!nameChanged || !nameValid || policyMutation.isPending}
              iconLeft={<Save size={15} />}
              size="sm"
              type="submit"
              variant="secondary"
            >
              {t.tenantAdmin.saveName}
            </Button>
          </div>
          {policyMutation.isSuccess ? <Banner status="success">{t.tenantAdmin.workspaceNameSaved}</Banner> : null}
          {policyMutation.isError ? <Banner status="error">{friendlyError(policyMutation.error, t)}</Banner> : null}
        </form>
      ) : null}
    </SettingsSection>
  );
}

/** Generic Agent defaults page. Private Agent identity/runtime lives on its own page. */
export function GenericAgentSettingsPanel({ enabled, session }: TenantSettingsPanelProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const tenantQueryScope = session?.tenantId ?? "anonymous";
  const tenantAgentSettingsQueryKey = ["tenant-agent-settings", tenantQueryScope] as const;
  const agentResearchEntitlementQueryKey = ["agent-research-entitlement", tenantQueryScope] as const;
  const agentSettingsQuery = useQuery({
    queryKey: tenantAgentSettingsQueryKey,
    queryFn: getTenantAgentSettings,
    enabled,
    retry: 1,
    staleTime: 30_000
  });
  const agentResearchEntitlementQuery = useQuery({
    queryKey: agentResearchEntitlementQueryKey,
    queryFn: getAgentResearchEntitlement,
    enabled,
    retry: 1,
    staleTime: 60_000
  });
  const agentSettingsMutation = useMutation({
    mutationFn: (draft: AgentSettingsDraft) => updateTenantAgentSettings(agentSettingsFromDraft(draft)),
    onSuccess: (response) => {
      queryClient.setQueryData(tenantAgentSettingsQueryKey, response);
    }
  });

  const agentResearchEntitlement = agentResearchEntitlementQuery.data?.entitlement ?? null;
  const privateAgentEntitlementChecking = enabled && !agentResearchEntitlement && agentResearchEntitlementQuery.isPending;
  const privateAgentEntitlementUnlocked = enabled && agentResearchEntitlement?.eligible === true;
  const privateAgentEntitlementVerificationFailed = enabled && agentResearchEntitlementQuery.isError;
  const agentSettingsLocked = !privateAgentEntitlementUnlocked;
  const agentSettingsLockMessage = !enabled
    ? t.tenantAdmin.signInAgentDefaults
    : privateAgentEntitlementChecking
      ? t.tenantAdmin.checkingAccess
      : privateAgentEntitlementVerificationFailed
        ? t.tenantAdmin.defaultsFrozen
        : agentSettingsLocked
          ? t.tenantAdmin.defaultsLocked
          : null;

  const agentSettingsForm = useForm({
    defaultValues: defaultAgentSettingsDraft,
    onSubmit: ({ value }) => {
      const draft = normalizeAgentSettingsDraft(value);
      if (
        !enabled ||
        agentSettingsLocked ||
        !agentSettingsDraftChanged(agentSettingsQuery.data?.settings ?? null, draft) ||
        agentSettingsMutation.isPending
      ) {
        return;
      }
      agentSettingsMutation.mutate(draft);
    }
  });

  useEffect(() => {
    const settings = agentSettingsQuery.data?.settings;
    if (settings) {
      agentSettingsForm.reset(draftFromAgentSettings(settings));
    }
  }, [agentSettingsForm, agentSettingsQuery.data?.settings]);

  const handleAgentSettingsSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void agentSettingsForm.handleSubmit();
  };

  return (
    <SettingsSection
      ariaLabel={t.tenantAdmin.agentSettingsAriaLabel}
      id="settings-agent"
      title={t.tenantAdmin.agentDefaults}
      trailing={
        agentSettingsQuery.isFetching || agentResearchEntitlementQuery.isFetching ? (
          <Spinner size={16} />
        ) : agentSettingsLocked ? (
          <Lock size={15} />
        ) : (
          <AgentAvatar size="xs" variant="generic" />
        )
      }
    >
      {agentSettingsQuery.isError ? <Banner status="error">{friendlyError(agentSettingsQuery.error, t)}</Banner> : null}
      {agentSettingsLockMessage ? <SettingsNote>{agentSettingsLockMessage}</SettingsNote> : null}
      {enabled && agentSettingsQuery.isLoading ? <Spinner label={t.tenantAdmin.loadingDefaults} size={16} /> : null}
      {enabled && agentSettingsQuery.data ? (
        <form onSubmit={handleAgentSettingsSubmit} style={{ display: "grid", gap: "var(--space-4)" }}>
          <agentSettingsForm.Field name="locale">
            {(field) => (
              <Select
                disabled={agentSettingsLocked}
                label={t.tenantAdmin.language}
                onChange={(value) => {
                  const nextLocale = value as Locale;
                  field.handleChange(nextLocale);
                  const nextRegion = regionOptionsByLocale[nextLocale][0] ?? "";
                  agentSettingsForm.setFieldValue("region", nextRegion);
                  if (nextLocale === "zh-CN") {
                    agentSettingsForm.setFieldValue("timezone", "Asia/Shanghai");
                  } else if (nextLocale === "zh-TW" && nextRegion === "HK") {
                    agentSettingsForm.setFieldValue("timezone", "Asia/Hong_Kong");
                  } else if (nextLocale === "zh-TW") {
                    agentSettingsForm.setFieldValue("timezone", "Asia/Taipei");
                  }
                }}
                options={localeOptions.map((locale) => ({ label: agentLocaleLabel(locale, t), value: locale }))}
                value={field.state.value}
              />
            )}
          </agentSettingsForm.Field>
          <agentSettingsForm.Subscribe selector={(state) => state.values.locale}>
            {(locale) => (
              <agentSettingsForm.Field name="region">
                {(field) => (
                  <Select
                    disabled={agentSettingsLocked}
                    label={t.tenantAdmin.region}
                    onChange={(value) => {
                      field.handleChange(value);
                      if (locale === "zh-TW" && value === "HK") {
                        agentSettingsForm.setFieldValue("timezone", "Asia/Hong_Kong");
                      } else if (locale === "zh-TW" && value === "TW") {
                        agentSettingsForm.setFieldValue("timezone", "Asia/Taipei");
                      }
                    }}
                    options={regionOptionsByLocale[locale].map((region) => ({ label: region, value: region }))}
                    value={field.state.value}
                  />
                )}
              </agentSettingsForm.Field>
            )}
          </agentSettingsForm.Subscribe>
          <agentSettingsForm.Field name="timezone">
            {(field) => (
              <Select
                disabled={agentSettingsLocked}
                label={t.tenantAdmin.timezone}
                onChange={(value) => field.handleChange(value)}
                options={timezoneOptions.map((timezone) => ({ label: timezone, value: timezone }))}
                value={field.state.value}
              />
            )}
          </agentSettingsForm.Field>
          <agentSettingsForm.Subscribe selector={(state) => state.values}>
            {(values) => {
              const draft = normalizeAgentSettingsDraft(values);
              const settingsChanged = agentSettingsDraftChanged(agentSettingsQuery.data?.settings ?? null, draft);
              return (
                <div>
                  <Button
                    disabled={agentSettingsLocked || !settingsChanged || agentSettingsMutation.isPending}
                    iconLeft={<Save size={15} />}
                    size="sm"
                    type="submit"
                    variant="secondary"
                  >
                    {t.tenantAdmin.saveDefaults}
                  </Button>
                </div>
              );
            }}
          </agentSettingsForm.Subscribe>
          {agentSettingsMutation.isSuccess ? <Banner status="success">{t.tenantAdmin.defaultsSaved}</Banner> : null}
          {agentSettingsMutation.isError ? <Banner status="error">{friendlyError(agentSettingsMutation.error, t)}</Banner> : null}
        </form>
      ) : null}
    </SettingsSection>
  );
}

/** Data-export-only page. Frame import/export tools remain on Advanced. */
export function DataExportSettingsPanel({ enabled }: { enabled: boolean }) {
  const { t } = useI18n();
  const tenantZipExportMutation = useMutation({
    mutationFn: exportTenantPrivacyZip,
    onSuccess: (response) => {
      downloadBlobArtifact(response.filename, response.blob);
    }
  });

  return (
    <SettingsSection
      ariaLabel={t.tenantAdmin.dataExportAriaLabel}
      id="settings-data"
      title={t.tenantAdmin.dataExport}
      trailing={tenantZipExportMutation.isPending ? <Spinner size={16} /> : <Download size={15} />}
    >
      <SettingsNote tone="body">{t.tenantAdmin.dataExportDescription}</SettingsNote>
      <div>
        <Button
          disabled={!enabled || tenantZipExportMutation.isPending}
          iconLeft={<Download size={15} />}
          onClick={() => tenantZipExportMutation.mutate()}
          size="sm"
          variant="secondary"
        >
          {t.tenantAdmin.exportZip}
        </Button>
      </div>
      {tenantZipExportMutation.isSuccess ? <Banner status="success">{t.tenantAdmin.exportStarted}</Banner> : null}
      {tenantZipExportMutation.isError ? <Banner status="error">{friendlyError(tenantZipExportMutation.error, t)}</Banner> : null}
    </SettingsSection>
  );
}

function draftFromAgentSettings(settings: SaleskoAgentSettings): AgentSettingsDraft {
  return {
    locale: settings.localeSeed.locale,
    messagingAddonEnabled: settings.messagingAddonEnabled,
    messagingChannels: settings.messagingChannels,
    region: settings.localeSeed.region ?? regionOptionsByLocale[settings.localeSeed.locale][0] ?? "",
    timezone: settings.localeSeed.timezone ?? timezoneForLocaleRegion(settings.localeSeed.locale, settings.localeSeed.region)
  };
}

function normalizeAgentSettingsDraft(draft: AgentSettingsDraft): AgentSettingsDraft {
  const locale = draft.locale;
  const allowedRegions = regionOptionsByLocale[locale];
  const region = allowedRegions.includes(draft.region) ? draft.region : allowedRegions[0] ?? "";
  return {
    locale,
    messagingAddonEnabled: draft.messagingAddonEnabled,
    messagingChannels: draft.messagingAddonEnabled ? uniqueMessagingChannels(draft.messagingChannels) : [],
    region,
    timezone: draft.timezone.trim() || timezoneForLocaleRegion(locale, region)
  };
}

function agentSettingsFromDraft(draft: AgentSettingsDraft): SaleskoAgentSettings {
  const normalized = normalizeAgentSettingsDraft(draft);
  return {
    localeSeed: {
      locale: normalized.locale,
      ...(normalized.region ? { region: normalized.region } : {}),
      ...(normalized.timezone ? { timezone: normalized.timezone } : {})
    },
    messagingAddonEnabled: normalized.messagingAddonEnabled,
    messagingChannels: normalized.messagingChannels
  };
}

function agentSettingsDraftChanged(settings: SaleskoAgentSettings | null, draft: AgentSettingsDraft) {
  if (!settings) {
    return false;
  }
  return JSON.stringify(agentSettingsFromDraft(draft)) !== JSON.stringify(settings);
}

function uniqueMessagingChannels(channels: AgentResearchMessagingChannel[]) {
  return messagingChannelOptions.filter((channel) => channels.includes(channel));
}

function timezoneForLocaleRegion(locale: Locale, region?: string) {
  if (locale === "zh-CN") {
    return "Asia/Shanghai";
  }
  if (locale === "zh-TW" && region === "HK") {
    return "Asia/Hong_Kong";
  }
  if (locale === "zh-TW") {
    return "Asia/Taipei";
  }
  return "America/Los_Angeles";
}

function agentLocaleLabel(value: Locale, t: Dictionary) {
  return t.tenantAdmin.locales[value];
}

function friendlyError(error: unknown, t: Dictionary) {
  if (!(error instanceof Error)) {
    return t.tenantAdmin.requestFailed;
  }
  const parsed = apiError(error);
  if (parsed?.message) {
    return parsed.message;
  }
  return error.message || t.tenantAdmin.requestFailed;
}

function apiError(error: Error) {
  try {
    const parsed = JSON.parse(error.message) as { error?: { code?: string; message?: string } };
    return parsed.error ?? null;
  } catch {
    return null;
  }
}
